import React, { useState, useEffect, useCallback } from "react";
import {
	Card,
	Table,
	Button,
	Tag,
	Badge,
	Space,
	Typography,
	Modal,
	Progress,
	Row,
	Col,
	Statistic,
	Spin,
	Alert,
	Select,
	message,
	Flex,
	Form,
	DatePicker,
} from "antd";
import {
	ArrowLeftOutlined,
	BookOutlined,
	CalendarOutlined,
	UserOutlined,
	CheckCircleOutlined,
	VideoCameraOutlined,
	PlayCircleOutlined,
	ClockCircleOutlined,
	DownloadOutlined,
	EditOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import useAxios from "../../utilities/useAxios";
import { formatDate, formatCurrency } from "../../utilities/Utils";
import PageTitle from "../PageTitle";
import { useNavigate, useParams } from "react-router-dom";
import XLSX from "xlsx-js-style";
import useIsMobile from "../../hooks/useIsMobile";

const { Title, Text } = Typography;

const PAGE_SIZE_OPTIONS = ["10", "100", "500"];

const pageShellStyle = {
	padding: "24px",
	background: "#f0f2f5",
	minHeight: "100vh",
	boxSizing: "border-box",
};

const AdminCourseDetailPage = () => {
	const isMobile = useIsMobile();
	const { courseId } = useParams();
	const navigate = useNavigate();
	const [detail, setDetail] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [selectedUser, setSelectedUser] = useState(null);
	const [showVideoProgressModal, setShowVideoProgressModal] =
		useState(false);
	const [videoProgressData, setVideoProgressData] = useState(null);
	const [videoProgressLoading, setVideoProgressLoading] = useState(false);
	const [periodModalOpen, setPeriodModalOpen] = useState(false);
	const [periodSaving, setPeriodSaving] = useState(false);
	const [periodForm] = Form.useForm();

	const fetchDetail = useCallback(async () => {
		try {
			setLoading(true);
			const response = await useAxios.get(
				`/cma/admin/courses/${courseId}/detail/`,
			);
			setDetail(response.data);
			setError(null);
		} catch (err) {
			console.error("Error fetching course detail:", err);
			setError("강의 상세 정보를 불러오는데 실패했습니다.");
			setDetail(null);
		} finally {
			setLoading(false);
		}
	}, [courseId]);

	useEffect(() => {
		fetchDetail();
	}, [fetchDetail]);

	const fetchVideoProgress = async (cid, userId) => {
		try {
			setVideoProgressLoading(true);
			const response = await useAxios.get(
				`/cma/admin/courses/${cid}/users/${userId}/video-progress/`,
			);
			setVideoProgressData(response.data);
		} catch (err) {
			console.error("Error fetching video progress:", err);
		} finally {
			setVideoProgressLoading(false);
		}
	};

	const handleShowVideoProgress = (user) => {
		setSelectedUser(user);
		setShowVideoProgressModal(true);
		fetchVideoProgress(courseId, user.id);
	};

	const handleCloseVideoProgressModal = () => {
		setShowVideoProgressModal(false);
		setSelectedUser(null);
		setVideoProgressData(null);
	};

	const handleGoToExamManage = () => {
		navigate(`/cma/admin/courses/${courseId}/exam`);
	};

	const openPeriodModal = () => {
		if (!detail?.course) return;
		const { start_date, end_date } = detail.course;
		periodForm.setFieldsValue({
			start_date: start_date ? dayjs(start_date) : null,
			end_date: end_date ? dayjs(end_date) : null,
		});
		setPeriodModalOpen(true);
	};

	const handlePeriodSubmit = async (values) => {
		try {
			setPeriodSaving(true);
			const response = await useAxios.put(
				`/cma/admin/courses/${courseId}/`,
				{
					start_date:
						values.start_date.format("YYYY-MM-DD"),
					end_date: values.end_date.format("YYYY-MM-DD"),
				},
			);
			setDetail((prev) => {
				if (!prev) return prev;
				return {
					...prev,
					course: response.data.course,
				};
			});
			message.success("수강 기간이 저장되었습니다.");
			setPeriodModalOpen(false);
		} catch (err) {
			const msg =
				err.response?.data?.end_date?.[0] ||
				err.response?.data?.start_date?.[0] ||
				err.response?.data?.non_field_errors?.[0] ||
				err.response?.data?.error ||
				"수강 기간 저장에 실패했습니다.";
			message.error(
				typeof msg === "string" ? msg : "수강 기간 저장에 실패했습니다.",
			);
		} finally {
			setPeriodSaving(false);
		}
	};

	const handleUpdateEnrollmentResult = async (enrollmentId, result) => {
		try {
			await useAxios.put(
				`/cma/admin/enrollments/${enrollmentId}/result/`,
				{ result },
			);
			setDetail((prev) => {
				if (!prev) return prev;
				return {
					...prev,
					users: (prev.users || []).map((u) =>
						u.enrollment_id === enrollmentId
							? { ...u, result }
							: u,
					),
				};
			});
			message.success("수료 상태가 변경되었습니다.");
		} catch (err) {
			message.error(
				err.response?.data?.error ||
					"수료 상태 변경에 실패했습니다.",
			);
		}
	};

	const getProgressColor = (progress) => {
		if (progress >= 80) return "#52c41a";
		if (progress >= 50) return "#faad14";
		return "#2457e6";
	};

	const getExamResultRank = (examResult) => {
		switch (examResult) {
			case "합격":
				return 3;
			case "불합격":
				return 2;
			case "미응시":
				return 1;
			default:
				return 0;
		}
	};

	const handleExportEnrollments = () => {
		if (!detail?.users?.length) {
			message.warning("내보낼 수강생 데이터가 없습니다.");
			return;
		}
		try {
			const date = new Date();
			const today = date.toISOString().split("T")[0];
			const courseName = detail.course?.name || "강의";
			const safeName = courseName.replace(/[/\\:*?"<>|]/g, "_");

			const wb = XLSX.utils.book_new();
			const headerStyle = {
				font: {
					bold: true,
					color: { rgb: "000000" },
					sz: 13,
				},
				fill: { fgColor: { rgb: "FAF4DB" } },
				alignment: {
					horizontal: "center",
					vertical: "center",
				},
				border: {
					left: {
						style: "thin",
						color: { auto: 1 },
					},
					right: {
						style: "thin",
						color: { auto: 1 },
					},
					top: {
						style: "thin",
						color: { auto: 1 },
					},
					bottom: {
						style: "thin",
						color: { auto: 1 },
					},
				},
			};

			const dataStyle = {
				font: { color: { rgb: "000000" }, sz: 11 },
				fill: { fgColor: { rgb: "FFFFFF" } },
				alignment: {
					horizontal: "center",
					vertical: "center",
				},
				border: {
					left: {
						style: "thin",
						color: { auto: 1 },
					},
					right: {
						style: "thin",
						color: { auto: 1 },
					},
					top: {
						style: "thin",
						color: { auto: 1 },
					},
					bottom: {
						style: "thin",
						color: { auto: 1 },
					},
				},
			};

			const headerRow = [
				{ v: "이름", t: "s", s: headerStyle },
				{ v: "결제상태", t: "s", s: headerStyle },
				{ v: "전화번호", t: "s", s: headerStyle },
				{ v: "이메일", t: "s", s: headerStyle },
				{ v: "진도율(%)", t: "s", s: headerStyle },
				{ v: "시청시간(분)", t: "s", s: headerStyle },
				{ v: "전체영상(분)", t: "s", s: headerStyle },
				{ v: "수료결과", t: "s", s: headerStyle },
				{ v: "시험결과", t: "s", s: headerStyle },
				{ v: "시험점수", t: "s", s: headerStyle },
				{ v: "결제일", t: "s", s: headerStyle },
				{ v: "결제금액", t: "s", s: headerStyle },
			];

			const dataRows = detail.users.map((u) => [
				{ v: u.name || "", t: "s", s: dataStyle },
				{
					v: u.is_paid ? "결제완료" : "미결제",
					t: "s",
					s: dataStyle,
				},
				{ v: u.phone_number || "", t: "s", s: dataStyle },
				{ v: u.email || "", t: "s", s: dataStyle },
				{
					v:
						u.progress_percentage != null
							? u.progress_percentage
							: "",
					t: "n",
					s: dataStyle,
				},
				{
					v: Math.round((u.total_watched_time || 0) / 60),
					t: "n",
					s: dataStyle,
				},
				{
					v: Math.round((u.total_video_duration || 0) / 60),
					t: "n",
					s: dataStyle,
				},
				{ v: u.result || "", t: "s", s: dataStyle },
				{ v: u.exam_result || "", t: "s", s: dataStyle },
				u.exam_score != null && u.exam_score !== ""
					? {
							v: Number(u.exam_score),
							t: "n",
							s: dataStyle,
						}
					: { v: "", t: "s", s: dataStyle },
				{
					v: u.payment_date ? formatDate(u.payment_date) : "",
					t: "s",
					s: dataStyle,
				},
				{
					v:
						u.payment_amount != null
							? u.payment_amount
							: "",
					t: "n",
					s: dataStyle,
				},
			]);

			const ws = XLSX.utils.aoa_to_sheet([
				headerRow,
				...dataRows,
			]);
			XLSX.utils.book_append_sheet(wb, ws, "수강생목록");
			XLSX.writeFile(wb, `${safeName}_수강생_${today}.xlsx`);
			message.success("엑셀 파일을 저장했습니다.");
		} catch (err) {
			console.error(err);
			message.error("엑셀 내보내기 중 오류가 발생했습니다.");
		}
	};

	const userColumns = [
		{
			title: "이름",
			dataIndex: "name",
			key: "name",
			render: (text, record) => (
				<Space>
					<UserOutlined />
					<Text strong>{text}</Text>
					{record.is_paid && (
						<Badge
							status="success"
							text="결제완료"
						/>
					)}
				</Space>
			),
		},
		{
			title: "연락처",
			key: "contact",
			render: (_, record) => (
				<Space direction="vertical" size={0}>
					<Text>{record.phone_number}</Text>
					<Text type="secondary">
						{record.email}
					</Text>
				</Space>
			),
		},
		{
			title: "진도율",
			dataIndex: "progress_percentage",
			key: "progress",
			render: (progress, record) => (
				<div style={{ width: "150px" }}>
					<Progress
						percent={progress}
						size="small"
						strokeColor={getProgressColor(
							progress,
						)}
						format={(percent) =>
							`${percent}%`
						}
					/>
					<Text
						type="secondary"
						style={{ fontSize: "11px" }}>
						{Math.round(
							record.total_watched_time /
								60,
						)}
						분 /{" "}
						{Math.round(
							record.total_video_duration /
								60,
						)}
						분
					</Text>
				</div>
			),
		},
		{
			title: "결과",
			dataIndex: "result",
			key: "result",
			render: (result, record) => (
				<Select
					size="small"
					style={{ width: 110 }}
					value={result}
					options={[
						{
							value: "수료",
							label: "수료",
						},
						{
							value: "미수료",
							label: "미수료",
						},
					]}
					onChange={(value) =>
						handleUpdateEnrollmentResult(
							record.enrollment_id,
							value,
						)
					}
				/>
			),
		},
		{
			title: "시험 결과",
			dataIndex: "exam_result",
			key: "exam_result",
			sorter: (a, b) =>
				getExamResultRank(a.exam_result) -
				getExamResultRank(b.exam_result),
			render: (examResult) => {
				if (examResult === "합격") {
					return <Tag color="green">합격</Tag>;
				}
				if (examResult === "불합격") {
					return <Tag color="red">불합격</Tag>;
				}
				if (examResult === "미응시") {
					return <Tag color="default">미응시</Tag>;
				}
				return <Tag>시험없음</Tag>;
			},
		},
		{
			title: "결제일",
			dataIndex: "payment_date",
			key: "payment_date",
			render: (date) => formatDate(date),
		},
		{
			title: "결제금액",
			dataIndex: "payment_amount",
			key: "payment_amount",
			render: (amount) => formatCurrency(amount),
		},
		{
			title: "영상별 진도",
			key: "video_progress",
			render: (_, record) => (
				<Button
					type="primary"
					size="small"
					icon={<VideoCameraOutlined />}
					onClick={() =>
						handleShowVideoProgress(record)
					}>
					영상별 진도
				</Button>
			),
		},
	];

	const course = detail?.course;

	const unpaidUsers = Math.max(
		0,
		(detail?.total_users ?? 0) - (detail?.paid_users ?? 0),
	);

	if (loading) {
		return (
			<div
				style={{
					...pageShellStyle,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}>
				<Spin size="large" />
			</div>
		);
	}

	if (error || !course) {
		return (
			<div style={pageShellStyle}>
				<PageTitle title="기독사관학교 - 강의 상세" />
				<Card>
					<Space direction="vertical" size="middle">
						<Button
							type="link"
							icon={<ArrowLeftOutlined />}
							onClick={() => navigate("/cma/admin")}
							style={{ padding: 0 }}>
							강의 관리로 돌아가기
						</Button>
						<Alert
							message="오류"
							description={
								error ||
								"강의를 찾을 수 없습니다."
							}
							type="error"
							showIcon
						/>
					</Space>
				</Card>
			</div>
		);
	}

	return (
		<div style={pageShellStyle}>
			<PageTitle
				title={`기독사관학교 - ${course.name}`}
			/>

			<Flex vertical gap={24}>
				<div>
					<Flex
						justify="space-between"
						align="flex-start"
						wrap="wrap"
						gap={16}>
						<div style={{ flex: "1 1 280px" }}>
							<Button
								icon={<ArrowLeftOutlined />}
								onClick={() =>
									navigate("/cma/admin")
								}>
								강의 목록
							</Button>
							<Title
								level={3}
								style={{
									marginTop: 16,
									marginBottom: 8,
								}}>
								{course.name}
							</Title>
							{course.book_title ? (
								<Text
									type="secondary"
									style={{
										fontSize: 15,
										display: "block",
										marginBottom: 12,
									}}>
									<BookOutlined
										style={{
											marginRight: 8,
										}}
									/>
									{course.book_title}
								</Text>
							) : null}
							<Space size={[16, 8]} wrap align="center">
								<Text>
									<CalendarOutlined
										style={{
											marginRight: 6,
										}}
									/>
									{formatDate(
										course.start_date,
									)}{" "}
									~{" "}
									{formatDate(
										course.end_date,
									)}
								</Text>
								<Button
									type="default"
									size="small"
									icon={<EditOutlined />}
									onClick={openPeriodModal}>
									수강 기간 수정
								</Button>
								<Tag
									color={
										course.type ===
										"온라인"
											? "blue"
											: "green"
									}>
									{course.type}
								</Tag>
								<Text type="secondary">
									정식{" "}
									{formatCurrency(
										course.fee_official,
									)}
									{" · "}청강{" "}
									{formatCurrency(
										course.fee_auditing,
									)}
								</Text>
							</Space>
						</div>
						<Button
							type="primary"
							size="large"
							onClick={handleGoToExamManage}>
							시험 관리
						</Button>
					</Flex>
				</div>

				<Card title="수강 현황">
					<Row gutter={[24, 24]}>
						<Col xs={24} sm={12} lg={6}>
							<Statistic
								title="총 수강생"
								value={detail.total_users}
								prefix={<UserOutlined />}
							/>
						</Col>
						<Col xs={24} sm={12} lg={6}>
							<Statistic
								title="결제 완료"
								value={detail.paid_users}
								prefix={<CheckCircleOutlined />}
							/>
						</Col>
						<Col xs={24} sm={12} lg={6}>
							<Statistic
								title="미결제"
								value={unpaidUsers}
							/>
						</Col>
						<Col xs={24} sm={12} lg={6}>
							<Statistic
								title="정식 수강료"
								value={formatCurrency(
									course.fee_official,
								)}
							/>
						</Col>
					</Row>
				</Card>

				<Card
					title="수강생 목록"
					extra={
						<Button
							icon={<DownloadOutlined />}
							onClick={
								handleExportEnrollments
							}>
							수강생 정보 내보내기
						</Button>
					}>
					<Table
						columns={userColumns}
						dataSource={detail.users}
						rowKey="id"
						scroll={{ x: "max-content" }}
						pagination={{
							pageSize: 10,
							showSizeChanger: true,
							pageSizeOptions:
								PAGE_SIZE_OPTIONS,
							showQuickJumper: true,
							showTotal: (total, range) =>
								`${range[0]}-${range[1]} / 총 ${total}명`,
						}}
					/>
				</Card>
			</Flex>

			<Modal
				title="수강 기간 수정"
				open={periodModalOpen}
				onCancel={() => setPeriodModalOpen(false)}
				footer={null}
				destroyOnClose
				afterClose={() => periodForm.resetFields()}>
				<Form
					form={periodForm}
					layout="vertical"
					onFinish={handlePeriodSubmit}
					autoComplete="off">
					<Form.Item
						name="start_date"
						label="시작일"
						rules={[
							{
								required: true,
								message: "시작일을 선택하세요.",
							},
						]}>
						<DatePicker
							inputReadOnly={isMobile}
							style={{ width: "100%" }}
							format="YYYY-MM-DD"
						/>
					</Form.Item>
					<Form.Item
						name="end_date"
						label="종료일"
						dependencies={["start_date"]}
						rules={[
							{
								required: true,
								message: "종료일을 선택하세요.",
							},
							({ getFieldValue }) => ({
								validator(_, value) {
									const start =
										getFieldValue(
											"start_date",
										);
									if (
										!value ||
										!start
									) {
										return Promise.resolve();
									}
									if (
										value.isBefore(
											start,
											"day",
										)
									) {
										return Promise.reject(
											new Error(
												"종료일은 시작일 이후여야 합니다.",
											),
										);
									}
									return Promise.resolve();
								},
							}),
						]}>
						<DatePicker
							inputReadOnly={isMobile}
							style={{ width: "100%" }}
							format="YYYY-MM-DD"
						/>
					</Form.Item>
					<Form.Item style={{ marginBottom: 0 }}>
						<Space>
							<Button
								type="primary"
								htmlType="submit"
								loading={periodSaving}>
								저장
							</Button>
							<Button
								onClick={() =>
									setPeriodModalOpen(false)
								}>
								취소
							</Button>
						</Space>
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				title={
					selectedUser ? (
						<Space align="center">
							<VideoCameraOutlined />
							<span>
								{selectedUser.name} · 영상별
								진도
							</span>
						</Space>
					) : (
						""
					)
				}
				open={showVideoProgressModal}
				onCancel={handleCloseVideoProgressModal}
				footer={
					<Button
						onClick={handleCloseVideoProgressModal}>
						닫기
					</Button>
				}
				width={isMobile ? "100%" : 720}
				styles={{
					body: { paddingTop: 12 },
				}}
				destroyOnClose>
				{videoProgressLoading ? (
					<div
						style={{
							textAlign: "center",
							padding: "48px 0",
						}}>
						<Spin size="large" />
					</div>
				) : videoProgressData ? (
					<Flex vertical gap={20}>
						<div
							style={{
								background: "#fafafa",
								borderRadius: 8,
								padding: "20px 24px",
								border:
									"1px solid #f0f0f0",
							}}>
							<Row gutter={[24, 24]}>
								<Col xs={24} sm={12} lg={6}>
									<Statistic
										title="전체 영상"
										value={
											videoProgressData
												.statistics
												.total_videos
										}
										prefix={
											<VideoCameraOutlined />
										}
									/>
								</Col>
								<Col xs={24} sm={12} lg={6}>
									<Statistic
										title="완료된 영상"
										value={
											videoProgressData
												.statistics
												.completed_videos
										}
										prefix={
											<CheckCircleOutlined />
										}
									/>
								</Col>
								<Col xs={24} sm={12} lg={6}>
									<Statistic
										title="전체 진도율"
										value={
											videoProgressData
												.statistics
												.overall_progress
										}
										suffix="%"
										prefix={
											<PlayCircleOutlined />
										}
									/>
								</Col>
								<Col xs={24} sm={12} lg={6}>
									<Statistic
										title="시청 시간"
										value={`${Math.round(
											videoProgressData
												.statistics
												.total_watched /
												60,
										)}분`}
										prefix={
											<ClockCircleOutlined />
										}
									/>
								</Col>
							</Row>
						</div>

						<div>
							<Title
								level={5}
								style={{
									marginTop: 0,
									marginBottom: 12,
								}}>
								영상별 진도 상세
							</Title>
							<Table
								columns={[
									{
										title: "강의",
										dataIndex:
											"lecture_name",
										key: "lecture_name",
										width: 150,
									},
									{
										title: "순서",
										dataIndex:
											"video_order",
										key: "video_order",
										width: 60,
										render: (order) =>
											`#${order}`,
									},
									{
										title: "영상명",
										dataIndex: "video_name",
										key: "video_name",
										ellipsis: true,
									},
									{
										title: "진도율",
										dataIndex:
											"progress_percentage",
										key: "progress",
										width: 120,
										render: (progress) => (
											<div
												style={{
													width: "100px",
												}}>
												<Progress
													percent={
														progress
													}
													size="small"
													strokeColor={getProgressColor(
														progress,
													)}
													format={(
														percent,
													) =>
														`${percent}%`
													}
												/>
											</div>
										),
									},
									{
										title: "시청 시간",
										key: "watched_time",
										width: 100,
										render: (
											_,
											record,
										) => (
											<Text
												type="secondary"
												style={{
													fontSize: 12,
												}}>
												{Math.round(
													record.total_watched_time /
														60,
												)}
												분 /{" "}
												{Math.round(
													record.duration /
														60,
												)}
												분
											</Text>
										),
									},
									{
										title: "상태",
										key: "status",
										width: 80,
										render: (
											_,
											record,
										) => (
											<Tag
												color={
													record.is_completed
														? "green"
														: "blue"
												}>
												{record.is_completed
													? "완료"
													: "진행중"}
											</Tag>
										),
									},
									{
										title: "마지막 시청",
										dataIndex: "updated_at",
										key: "updated_at",
										width: 100,
										render: (
											updated_at,
										) =>
											updated_at
												? formatDate(
														updated_at,
													)
												: "-",
									},
								]}
								dataSource={
									videoProgressData.videos
								}
								rowKey="video_id"
								pagination={{
									pageSize: 10,
									showSizeChanger: true,
									showQuickJumper: true,
									showTotal: (
										total,
										range,
									) =>
										`${range[0]}-${range[1]} / 총 ${total}개`,
								}}
								size="small"
							/>
						</div>
					</Flex>
				) : null}
			</Modal>
		</div>
	);
};

export default AdminCourseDetailPage;
